/**
 * MONSTER TOWN — VENUE ART KIT. OWNED BY THE VENUE CREW (src/venue/**).
 *
 * Chunky, code-only art for everything the shop needs: the food itself, the
 * stations that dispense it, the customers, and the picture-speech bubble.
 *
 * RULES INHERITED FROM `src/render/kit.ts` AND OBEYED HERE:
 *  - never `new THREE.Material()` — every surface comes from `toon`/`flat`,
 *    which cache by colour. A tub built 40 times must not cost 40 materials.
 *  - ink outlines on SILHOUETTES only (the body of a thing), never on details.
 *  - a builder is deterministic: same input, same mesh, every time.
 *
 * Everything here is driven by `VenueItem` DATA, so venue #2's food is a
 * content drop and this file does not change.
 */

import * as THREE from 'three'
import { MONSTER_BODY_COLORS, type PaletteName } from '../core/palette'
import type { MonsterConfig } from '../core/types'
import { ball, boxOn, cone, cylinder, group, inked } from '../render/kit'
import type { VenueItem, VenueOrder, VenueTray } from './types'

const SCOOP_R = 0.17
const SCOOP_STEP = 0.26

// ---------------------------------------------------------------------------
// the food
// ---------------------------------------------------------------------------

/** One scoop. The atom of counting. */
export function scoop(color: PaletteName, y: number, s = 1): THREE.Object3D {
  const g = group('scoop')
  g.add(ball(SCOOP_R * s, { color, y, seg: 10 }))
  // A tiny highlight blob so a scoop reads as round, not as a flat circle.
  g.add(ball(SCOOP_R * s * 0.42, { color: 'flowerWhite', x: -0.06 * s, y: y + 0.08 * s, z: 0.09 * s, seg: 8 }))
  return g
}

export function coneBase(s = 1): THREE.Object3D {
  const g = group('base_cone')
  // Flip it so the point is at the floor and the wide end holds the scoops.
  const body = cone(0.19 * s, 0.42 * s, { color: 'cone', seg: 10 })
  body.rotation.x = Math.PI
  body.position.y = 0.21 * s
  body.name = 'coneBody'
  g.add(inked(body))
  return g
}

export function cupBase(s = 1): THREE.Object3D {
  const g = group('base_cup')
  const body = cylinder(0.19 * s, 0.3 * s, { color: 'plate', seg: 10, name: 'cupBody' })
  g.add(inked(body))
  g.add(cylinder(0.2 * s, 0.05 * s, { color: 'uiAccent', y: 0.28 * s, seg: 10 }))
  return g
}

/**
 * A DINNER PLATE — wide, shallow, with a rim.
 *
 * The restaurant's plate used to be declared as a `cup`, so a plate of pasta was
 * served in an ice-cream tub and stacked into a vertical tower. A plate is a
 * different shape and it lays its food out SIDE BY SIDE (see `servingArt`),
 * which is the single clearest signal that this venue is not the ice cream shop.
 */
export function plateBase(s = 1): THREE.Object3D {
  const g = group('base_plate')
  const body = cylinder(0.3 * s, 0.05 * s, { color: 'plate', y: 0.025 * s, seg: 14, name: 'plateBody' })
  g.add(inked(body))
  g.add(cylinder(0.33 * s, 0.035 * s, { color: 'uiPanelAlt', y: 0.055 * s, seg: 14 }))
  g.add(cylinder(0.21 * s, 0.02 * s, { color: 'plate', y: 0.075 * s, seg: 14 }))
  return g
}

function toppingArt(item: VenueItem, y: number, s: number): THREE.Object3D {
  const g = group(`top_${item.id}`)
  switch (item.shape) {
    case 'cherry':
      g.add(ball(0.09 * s, { color: item.color, y, seg: 8 }))
      g.add(cylinder(0.015 * s, 0.12 * s, { color: 'leaf', y, seg: 5 }))
      break
    case 'sprinkles':
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2
        g.add(
          boxOn(0.045 * s, 0.03 * s, 0.02 * s, {
            color: i % 2 === 0 ? item.color : 'flowerYellow',
            x: Math.cos(a) * 0.12 * s,
            y: y - 0.04 * s + (i % 3) * 0.02 * s,
            z: Math.sin(a) * 0.12 * s,
            rotY: a,
          }),
        )
      }
      break
    case 'sauce':
    default:
      g.add(cylinder(0.15 * s, 0.05 * s, { color: item.color, y: y - 0.1 * s, seg: 10 }))
      g.add(ball(0.06 * s, { color: item.color, y: y - 0.02 * s, seg: 8 }))
      break
  }
  return g
}

/**
 * THE serving model — used for the tray the kid carries, the picture inside a
 * customer's speech bubble, and the display case. One builder, three places, so
 * what a kid is asked for and what they are holding always look the same.
 */
export function servingArt(
  parts: { base: string | null; scoops: readonly string[]; topping: string | null },
  items: readonly VenueItem[],
  s = 1,
): THREE.Object3D {
  const g = group('serving')
  const find = (id: string | null): VenueItem | undefined =>
    id === null ? undefined : items.find((i) => i.id === id)

  const baseItem = find(parts.base)
  // A PLATE SERVES SIDE BY SIDE; A CONE OR CUP STACKS. This one rule is what
  // makes a plate of dinner stop looking like a sundae, and it is driven by the
  // declared shape, so venue #3 gets it for free.
  const flat = baseItem?.shape === 'plate'
  let y = 0
  if (baseItem !== undefined) {
    if (flat) g.add(plateBase(s))
    else g.add(baseItem.shape === 'cup' ? cupBase(s) : coneBase(s))
    y = (flat ? 0.09 : baseItem.shape === 'cup' ? 0.3 : 0.45) * s
  } else {
    y = 0.12 * s
  }

  const n = parts.scoops.length
  for (let i = 0; i < n; i++) {
    const it = find(parts.scoops[i] ?? null)
    if (it === undefined) continue
    if (flat) {
      // Portions laid around the middle of the plate, never a tower.
      const a = n === 1 ? 0 : (i / n) * Math.PI * 2
      const r = n === 1 ? 0 : 0.13 * s
      const mound = scoop(it.color, y + SCOOP_R * s * 0.8, s * 0.92)
      mound.position.set(Math.cos(a) * r, 0, Math.sin(a) * r)
      g.add(mound)
    } else {
      g.add(scoop(it.color, y + SCOOP_R * s, s))
      y += SCOOP_STEP * s
    }
  }
  if (flat && n > 0) y += SCOOP_R * 1.7 * s

  const top = find(parts.topping)
  if (top !== undefined) g.add(toppingArt(top, y + 0.04 * s, s))
  return g
}

export function orderArt(order: VenueOrder, items: readonly VenueItem[], s = 1): THREE.Object3D {
  return servingArt({ base: order.base, scoops: order.scoops, topping: order.topping }, items, s)
}

export function trayArt(tray: VenueTray, items: readonly VenueItem[], s = 1): THREE.Object3D {
  return servingArt(tray, items, s)
}

// ---------------------------------------------------------------------------
// customers
// ---------------------------------------------------------------------------

/**
 * THE LOOK OF A VISITING MONSTER — a plain `MonsterConfig`, nothing more.
 *
 * The art itself is NOT built here. Customers used to be hand-rolled boxes in
 * this file: a 0.72-wide cube torso with eyes stuck on the front and two
 * matchstick horns. Standing next to the player — a rounded, jointed, animated
 * rig from the monster crew — they read as painted props rather than as
 * monsters, which is exactly the failure the brief warns about, and the two
 * systems had already drifted apart in silhouette, proportion and shading.
 *
 * So a customer IS a monster now: `customers.ts` feeds this config straight to
 * the monster crew's `buildMonster`, the one builder that makes every monster in
 * the game. That is a deliberate exception to "crews never import each other" —
 * `buildMonster` is a pure config-in / group-out factory in the same category as
 * `render/kit.ts`, and one shared character system is the whole point. There is
 * no longer any code in this crew that can draw a face.
 */
export function customerLook(rng: () => number): MonsterConfig {
  const pick = <T>(list: readonly T[], fallback: T): T =>
    list[Math.min(list.length - 1, Math.floor(rng() * list.length))] ?? fallback
  const color = pick(MONSTER_BODY_COLORS, 'monsterCoral')
  const accents: PaletteName[] = ['monsterBelly', 'monsterCream', 'flowerPink', 'flowerYellow', 'wallBlue']
  let accent = pick(accents, 'monsterBelly')
  if (accent === color) accent = 'monsterBelly'
  return {
    shape: pick(['blob', 'tall', 'round', 'chunky'] as const, 'blob'),
    color,
    accentColor: accent,
    eyes: pick(['two', 'one', 'three', 'sleepy'] as const, 'two'),
    horns: pick(['none', 'nubs', 'curved', 'antlers', 'antennae'] as const, 'nubs'),
    pattern: pick(['plain', 'spots', 'stripes', 'belly', 'star'] as const, 'belly'),
    hat: null,
    outfit: null,
  }
}

/**
 * "THIS ONE IS WAITING FOR YOU." — a ring on the floor, in the customer's own
 * body colour.
 *
 * HISTORY, BECAUSE THIS SPOT HAS BEEN WRONG TWICE.
 *
 * First it was a full picture-speech board billboarded over each head. It was a
 * nice object and it covered the faces of the monsters standing behind it.
 *
 * Then it was a small floating plate with three dots on it, and the order moved
 * to a rail of cards pinned across the top of the screen. That was worse in the
 * way that mattered most: the thing the kid has to remember — WHAT this monster
 * asked for — was drawn at the top of the screen with nothing tying it to the
 * monster, and all the monster itself said was "...". The venue's whole
 * listening game had no picture on the creature doing the asking.
 *
 * The order card now hangs directly over this monster's head and follows it
 * (see `CustomerAnchor`), so the room does not need a second copy of the order.
 * What it needs is the pairing signal, and a ring on the FLOOR is the one place
 * a marker can never hide a face, never overlap a neighbour's card, and still
 * read at a glance under a fixed iso camera.
 */
export function waitRing(color: PaletteName): THREE.Group {
  const g = group('waitRing')
  // Ink disc under a colour disc: the same trick the coin prices use, so a pale
  // monster's ring still separates from a pale floor.
  const rim = cylinder(0.62, 0.012, { color: 'ink', y: 0, seg: 20, name: 'rim' })
  g.add(rim)
  const face = cylinder(0.54, 0.014, { color, y: 0.006, seg: 20, name: 'face' })
  g.add(face)
  // A hole in the middle, so it reads as a ring around the monster's feet
  // rather than as a coloured puddle it is standing in.
  g.add(cylinder(0.36, 0.016, { color: 'uiPanel', y: 0.01, seg: 20, name: 'hole' }))
  // Three chunky nubs pointing inward: "over here", countable, unreadable-proof.
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2
    g.add(
      ball(0.09, {
        color,
        x: Math.cos(a) * 0.72,
        y: 0.02,
        z: Math.sin(a) * 0.72,
        seg: 7,
      }),
    )
  }
  return g
}

/** A little pop of hearts over a delighted customer. Built once, animated live. */
export function heartsBurst(): THREE.Group {
  const g = group('hearts')
  for (let i = 0; i < 5; i++) {
    const h = group(`heart${i}`)
    h.add(ball(0.09, { color: 'uiHeart', x: -0.05, y: 0.04, seg: 7 }))
    h.add(ball(0.09, { color: 'uiHeart', x: 0.05, y: 0.04, seg: 7 }))
    h.add(cone(0.1, 0.16, { color: 'uiHeart', y: -0.14, seg: 5 }))
    g.add(h)
  }
  return g
}

// ---------------------------------------------------------------------------
// coins — prices as PICTURES (brief: legible as coin icons, not just numerals)
// ---------------------------------------------------------------------------

/**
 * A PRICE YOU CAN COUNT. big = 100, middle = 10, little = 1.
 *
 * WHY THIS IS A GRID AND NOT A LINE. It used to lay every coin out in one row,
 * so a 45-coin price was nine discs side by side; the row was then shrunk to fit
 * the tag it sits on, and what a kid actually saw on a shop shelf was a smear of
 * gold dots about ten pixels across. Unreadable, and unreadable in the one place
 * in the game where a number decides whether you can have the thing.
 *
 * Rows of at most FIVE, biggest denomination on top, each row on its own line.
 * The widest a price can ever be is five coins, so the fit-to-tag shrink barely
 * fires and the discs stay finger-sized — and five-and-some is also how a kid
 * this age actually counts.
 */
const COINS_PER_ROW = 5

export function coinPrice(amount: number, s = 1): THREE.Group {
  const g = group('price')
  const bands: { r: number; n: number; color: PaletteName }[] = [
    { r: 0.15 * s, n: Math.min(Math.floor(amount / 100), 5), color: 'uiCoin' },
    { r: 0.115 * s, n: Math.min(Math.floor((amount % 100) / 10), 9), color: 'uiCoin' },
    { r: 0.08 * s, n: Math.min(amount % 10, 9), color: 'uiCoinDark' },
  ]

  const lines: { r: number; n: number; color: PaletteName }[] = []
  for (const b of bands) {
    for (let left = b.n; left > 0; left -= COINS_PER_ROW) {
      lines.push({ r: b.r, n: Math.min(COINS_PER_ROW, left), color: b.color })
    }
  }
  if (lines.length === 0) return g

  const gap = 0.06 * s
  let h = 0
  for (const l of lines) h += l.r * 2 + gap
  let y = h / 2 - (lines[0]?.r ?? 0)

  for (const l of lines) {
    const step = l.r * 2.45
    const x0 = -((l.n - 1) * step) / 2
    for (let i = 0; i < l.n; i++) {
      // A dark disc behind each coin so neighbouring coins never merge into one
      // gold sausage — you have to be able to COUNT them.
      const rim = cylinder(l.r * 1.2, 0.03 * s, { color: 'ink', x: x0 + i * step, y, z: -0.01 * s, seg: 12 })
      rim.rotation.x = Math.PI / 2
      g.add(rim)
      const c = cylinder(l.r, 0.035 * s, { color: l.color, x: x0 + i * step, y, seg: 12 })
      c.rotation.x = Math.PI / 2
      g.add(c)
    }
    y -= l.r * 2 + gap
  }
  return g
}

/** A single fat coin, for finds lying in the grass and for coin pops. */
export function coinArt(s = 1): THREE.Group {
  const g = group('coin')
  const disc = cylinder(0.32 * s, 0.1 * s, { color: 'uiCoin', seg: 14, name: 'disc' })
  disc.rotation.x = Math.PI / 2
  g.add(inked(disc))
  const inner = cylinder(0.19 * s, 0.13 * s, { color: 'uiCoinDark', seg: 12 })
  inner.rotation.x = Math.PI / 2
  g.add(inner)
  return g
}
